import { getEnv } from "@/env";

const URL_RESOLUTION_BASE = getEnv(
  "NEXT_PUBLIC_SITE_URL",
  "https://www.edx.org",
);
const MAX_NESTED_NEXT_DESTINATIONS = 5;

export const MARKETPLACE_ATTRIBUTION_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
] as const;

type MarketplaceAttributionSource = URLSearchParams | string;

type AppendMarketplaceAttributionParamsToHrefArgs = {
  href?: string;
  source: MarketplaceAttributionSource;
  includeNextDestination?: boolean;
};

const isHttpOrHttpsUrl = (href: string) => /^https?:\/\//i.test(href);

const isProtocolRelativeUrl = (href: string) => href.startsWith("//");

const isNonHttpProtocol = (href: string) =>
  /^[a-z][a-z0-9+.-]*:/i.test(href) && !isHttpOrHttpsUrl(href);

export const isTrustedEdxHostname = (hostname: string) =>
  hostname === "edx.org" || hostname.endsWith(".edx.org");

// Always copy: `useSearchParams()` returns a `ReadonlyURLSearchParams`, whose
// `set`/`append`/`delete`/`sort` throw. Copying keeps every value returned from
// this module safely mutable for callers that add their own params.
const toSearchParams = (source: MarketplaceAttributionSource) =>
  typeof source === "string"
    ? new URLSearchParams(source.replace(/^\?/, ""))
    : new URLSearchParams(source);

const formatUrl = (url: URL, href: string) => {
  if (isHttpOrHttpsUrl(href)) {
    return url.toString();
  }

  if (isProtocolRelativeUrl(href)) {
    return `//${url.host}${url.pathname}${url.search}${url.hash}`;
  }

  return `${url.pathname}${url.search}${url.hash}`;
};

export function getMarketplaceAttributionParams(
  source: MarketplaceAttributionSource,
): URLSearchParams {
  const sourceParams = toSearchParams(source);
  const attributionParams = new URLSearchParams();

  MARKETPLACE_ATTRIBUTION_PARAMS.forEach((key) => {
    const value = sourceParams.get(key);
    if (value) {
      attributionParams.set(key, value);
    }
  });

  return attributionParams;
}

export function hasMarketplaceAttributionParams(
  source: MarketplaceAttributionSource,
): boolean {
  return getMarketplaceAttributionParams(source).toString().length > 0;
}

/**
 * Prefer attribution present on the primary source (usually the current URL).
 * Fall back to a stored session source when the primary has none.
 */
export function resolveMarketplaceAttributionParams(
  primary: MarketplaceAttributionSource,
  fallback?: MarketplaceAttributionSource | null,
): URLSearchParams {
  const primaryParams = getMarketplaceAttributionParams(primary);
  if (primaryParams.toString()) {
    return primaryParams;
  }

  if (!fallback) {
    return primaryParams;
  }

  return getMarketplaceAttributionParams(fallback);
}

/**
 * Merge approved attribution params into a destination URL without overwriting
 * values already present on the destination.
 */
export function mergeMarketplaceAttributionIntoUrl(
  targetUrl: string | URL,
  source: MarketplaceAttributionSource,
  baseUrl?: string | URL,
): string {
  const url =
    typeof targetUrl === "string"
      ? new URL(targetUrl, baseUrl ?? URL_RESOLUTION_BASE)
      : new URL(targetUrl.toString());

  getMarketplaceAttributionParams(source).forEach((value, key) => {
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
}

function appendMarketplaceAttributionParams(
  {
    href = "/",
    source,
    includeNextDestination = false,
  }: AppendMarketplaceAttributionParamsToHrefArgs,
  nestedDestinationDepth: number,
): string {
  if (href.startsWith("#") || isNonHttpProtocol(href)) {
    return href;
  }

  const url = isHttpOrHttpsUrl(href)
    ? new URL(href)
    : new URL(href, URL_RESOLUTION_BASE);
  const siteOrigin = new URL(URL_RESOLUTION_BASE).origin;

  const isSiteOrigin = url.origin === siteOrigin;
  // Trusted edX subdomains (home, authn, checkout) always receive attribution
  // so edx.org ↔ home.edx.org handoffs do not drop campaign params.
  const isApprovedExternalOrigin = isTrustedEdxHostname(url.hostname);

  if (!isSiteOrigin && !isApprovedExternalOrigin) {
    return href;
  }

  getMarketplaceAttributionParams(source).forEach((value, key) => {
    if (!url.searchParams.has(key)) {
      url.searchParams.set(key, value);
    }
  });

  const nextDestination = url.searchParams.get("next");
  if (
    includeNextDestination &&
    nextDestination &&
    nestedDestinationDepth < MAX_NESTED_NEXT_DESTINATIONS
  ) {
    url.searchParams.set(
      "next",
      appendMarketplaceAttributionParams(
        {
          href: nextDestination,
          source,
          includeNextDestination: true,
        },
        nestedDestinationDepth + 1,
      ),
    );
  }

  return formatUrl(url, href);
}

export function appendMarketplaceAttributionParamsToHref(
  args: AppendMarketplaceAttributionParamsToHrefArgs,
): string {
  return appendMarketplaceAttributionParams(args, 0);
}
